/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ContactsGeoBuilder.tsx - Constructor de Audiencias GEOMARKETING GENIUS MODE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 3.7.0 - MULTI-WASP SYSTEM
 * @description Sistema revolucionario de targeting geográfico con múltiples zonas
 * 
 * ARQUITECTURA v3.7.0:
 * ✅ WASP draggable Canvas ↔ Mapa (IMPLEMENTADO)
 * ✅ Herramientas de dibujo: círculo + polígono (GEOMAN INTEGRADO)
 * ✅ Filtrado geográfico activo (IMPLEMENTADO)
 * ✅ Modo Canvas vs Modo Mapa (IMPLEMENTADO)
 * ✅ Contador contactos en zona (IMPLEMENTADO)
 * ✅ Indicadores visuales de modo (IMPLEMENTADO)
 * ✅ Arquitectura 4 capas independientes
 * ✅ Elementos movibles después de soltar
 * ✅ Modal custom Aqua Apple
 * ✅ Contactos auto-seleccionados al finalizar con geo-filtro
 * 
 * NUEVO EN v3.7.0 - MULTI-WASP SYSTEM:
 * ✅ Triple-click en WASP madre → Spawn WASP hija
 * ✅ Triple-click en WASP hija → Eliminar WASP hija
 * ✅ Cada WASP tiene su propia zona geográfica
 * ✅ Selector de WASP activa en sidebar
 * ✅ OR lógico multi-zona (contacto en zona1 OR zona2 OR zona3)
 * ✅ Máximo 5 WASPs hijas por sesión
 * ✅ Colores únicos por WASP para identificación visual
 * ✅ Resumen Multi-Zona con conteo total único
 * 
 * @date 2024-11-21
 * @author flux - GENIO DEL GEOMARKETING
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkedAlt,
  faTimes,
  faCheckCircle,
  faMagic,
  faNetworkWired,
  faProjectDiagram,
  faDrawPolygon,
  faCircle
} from '@fortawesome/free-solid-svg-icons';

// Tipos
import type { ContactDB } from '../../../data/mockContactsDatabase';

// Assets
import wasplogo from '../../../assets/img/Anubis/WASP.png';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface GeoZone {
  type: 'circle' | 'polygon';
  coordinates: { lat: number; lng: number }[];
  radius?: number; // Para círculos (en metros)
  layer?: any; // Leaflet layer reference
}

// ═══════════════════════════════════════════════════════════════════════════
// FUNCIONES GEOGRÁFICAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula si un punto está dentro de un círculo usando fórmula Haversine
 * @param point Punto a verificar
 * @param center Centro del círculo
 * @param radiusMeters Radio en metros
 * @returns true si el punto está dentro del círculo
 */
const isPointInCircle = (
  point: { lat: number; lng: number },
  center: { lat: number; lng: number },
  radiusMeters: number
): boolean => {
  const R = 6371e3; // Radio de la Tierra en metros
  const φ1 = point.lat * Math.PI / 180;
  const φ2 = center.lat * Math.PI / 180;
  const Δφ = (center.lat - point.lat) * Math.PI / 180;
  const Δλ = (center.lng - point.lng) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
           Math.cos(φ1) * Math.cos(φ2) *
           Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;

  return distance <= radiusMeters;
};

/**
 * Calcula si un punto está dentro de un polígono usando Ray Casting Algorithm
 * @param point Punto a verificar
 * @param polygon Vértices del polígono
 * @returns true si el punto está dentro del polígono
 */
const isPointInPolygon = (
  point: { lat: number; lng: number },
  polygon: { lat: number; lng: number }[]
): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng, yi = polygon[i].lat;
    const xj = polygon[j].lng, yj = polygon[j].lat;
    
    const intersect = ((yi > point.lat) !== (yj > point.lat))
        && (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

/**
 * Filtra contactos que están dentro de una zona geográfica
 * @param contacts Lista de contactos
 * @param zone Zona geográfica (círculo o polígono)
 * @returns Contactos dentro de la zona
 */
const filterContactsByGeoZone = (
  contacts: ContactDB[],
  zone: GeoZone
): ContactDB[] => {
  return contacts.filter(contact => {
    if (!contact.location) return false;
    
    if (zone.type === 'circle') {
      return isPointInCircle(
        contact.location,
        zone.coordinates[0],
        zone.radius!
      );
    } else {
      return isPointInPolygon(
        contact.location,
        zone.coordinates
      );
    }
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// SVG ASSETS
// ═══════════════════════════════════════════════════════════════════════════

const WASP_NODE_SVG = `
<svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="wasp-glow" cx="50%" cy="50%" r="50%">
      <stop offset="0%" style="stop-color:#2ca799;stop-opacity:0.8" />
      <stop offset="50%" style="stop-color:#2ca799;stop-opacity:0.4" />
      <stop offset="100%" style="stop-color:#2ca799;stop-opacity:0" />
    </radialGradient>
    
    <radialGradient id="wasp-core" cx="50%" cy="50%" r="50%">
      <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
      <stop offset="40%" style="stop-color:#2ca799;stop-opacity:0.9" />
      <stop offset="100%" style="stop-color:#238a7f;stop-opacity:0.6" />
    </radialGradient>

    <filter id="glow">
      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
      <feMerge>
        <feMergeNode in="coloredBlur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>

  <circle cx="40" cy="40" r="38" fill="url(#wasp-glow)" opacity="0.6">
    <animate attributeName="r" values="38;42;38" dur="2s" repeatCount="indefinite"/>
    <animate attributeName="opacity" values="0.6;0.9;0.6" dur="2s" repeatCount="indefinite"/>
  </circle>

  <g filter="url(#glow)">
    <ellipse cx="40" cy="40" rx="18" ry="24" fill="#ffcc00" stroke="#ff9900" stroke-width="2"/>
    <rect x="22" y="30" width="36" height="4" rx="2" fill="#000000" opacity="0.8"/>
    <rect x="22" y="38" width="36" height="4" rx="2" fill="#000000" opacity="0.8"/>
    <rect x="22" y="46" width="36" height="4" rx="2" fill="#000000" opacity="0.8"/>
    <circle cx="40" cy="22" r="8" fill="#ffcc00" stroke="#ff9900" stroke-width="2"/>
    <circle cx="36" cy="21" r="2.5" fill="#000000">
      <animate attributeName="opacity" values="1;0.3;1" dur="4s" repeatCount="indefinite"/>
    </circle>
    <circle cx="44" cy="21" r="2.5" fill="#000000">
      <animate attributeName="opacity" values="1;0.3;1" dur="4s" repeatCount="indefinite"/>
    </circle>
    <line x1="36" y1="16" x2="33" y2="10" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="44" y1="16" x2="47" y2="10" stroke="#000000" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="33" cy="10" r="1.5" fill="#2ca799">
      <animate attributeName="fill" values="#2ca799;#3b82f6;#2ca799" dur="1.5s" repeatCount="indefinite"/>
    </circle>
    <circle cx="47" cy="10" r="1.5" fill="#2ca799">
      <animate attributeName="fill" values="#3b82f6;#2ca799;#3b82f6" dur="1.5s" repeatCount="indefinite"/>
    </circle>
    <ellipse cx="25" cy="30" rx="12" ry="8" fill="#ffffff" opacity="0.7" stroke="#2ca799" stroke-width="1">
      <animateTransform attributeName="transform" type="rotate" values="0 25 30;-15 25 30;0 25 30" dur="0.3s" repeatCount="indefinite"/>
    </ellipse>
    <ellipse cx="23" cy="38" rx="10" ry="7" fill="#ffffff" opacity="0.6" stroke="#2ca799" stroke-width="1">
      <animateTransform attributeName="transform" type="rotate" values="0 23 38;-12 23 38;0 23 38" dur="0.3s" repeatCount="indefinite"/>
    </ellipse>
    <ellipse cx="55" cy="30" rx="12" ry="8" fill="#ffffff" opacity="0.7" stroke="#2ca799" stroke-width="1">
      <animateTransform attributeName="transform" type="rotate" values="0 55 30;15 55 30;0 55 30" dur="0.3s" repeatCount="indefinite"/>
    </ellipse>
    <ellipse cx="57" cy="38" rx="10" ry="7" fill="#ffffff" opacity="0.6" stroke="#2ca799" stroke-width="1">
      <animateTransform attributeName="transform" type="rotate" values="0 57 38;12 57 38;0 57 38" dur="0.3s" repeatCount="indefinite"/>
    </ellipse>
    <path d="M 40 64 L 38 70 L 40 75 L 42 70 Z" fill="#000000" opacity="0.8"/>
    <circle cx="40" cy="75" r="1.5" fill="#ef4444">
      <animate attributeName="fill" values="#ef4444;#f59e0b;#ef4444" dur="1s" repeatCount="indefinite"/>
    </circle>
  </g>

  <circle cx="40" cy="40" r="36" fill="none" stroke="#2ca799" stroke-width="0.5" opacity="0.8" stroke-dasharray="4,4">
    <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="10s" repeatCount="indefinite"/>
  </circle>
  
  <circle cx="40" cy="4" r="2" fill="#2ca799" opacity="0.8">
    <animate attributeName="opacity" values="0.8;0.3;0.8" dur="1.5s" repeatCount="indefinite"/>
  </circle>
  <circle cx="76" cy="40" r="2" fill="#2ca799" opacity="0.8">
    <animate attributeName="opacity" values="0.3;0.8;0.3" dur="1.5s" repeatCount="indefinite"/>
  </circle>
  <circle cx="40" cy="76" r="2" fill="#2ca799" opacity="0.8">
    <animate attributeName="opacity" values="0.8;0.3;0.8" dur="1.5s" repeatCount="indefinite"/>
  </circle>
  <circle cx="4" cy="40" r="2" fill="#2ca799" opacity="0.8">
    <animate attributeName="opacity" values="0.3;0.8;0.3" dur="1.5s" repeatCount="indefinite"/>
  </circle>

  <text x="40" y="85" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" font-size="6" fill="#2ca799" text-anchor="middle" opacity="0.7" font-weight="600">
    WASP
  </text>
</svg>
`;

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface ContactsGeoBuilderProps {
  contacts: ContactDB[];
  selectedContactIds: string[];
  onContactSelect: (idOrContact: string | ContactDB) => void; // ← Actualizar tipo
  onSelectionComplete: (finalContacts: ContactDB[]) => void;
  onClose: () => void;
}

interface ContactElement {
  id: string;
  type: 'favorites' | 'all-contacts';
  label: string;
  icon: string;
  position: { x: number; y: number };
  zone: 'canvas' | 'map';
  limit?: number; // Para 'all-contacts'
  contactIds: string[];
  color: string;
  locked: boolean; // Se bloquea solo al finalizar
}

interface WASPNode {
  x: number;
  y: number;
  size: number;
  dragging: boolean;
  zone: 'canvas' | 'map'; // NUEVO: WASP puede estar en canvas o mapa
  mapPosition?: { lat: number; lng: number }; // Posición en mapa
}

// ═══════════════════════════════════════════════════════════════════════════
// MULTI-WASP SYSTEM v3.7.0
// ═══════════════════════════════════════════════════════════════════════════

interface WASPInstance {
  id: string;
  x: number;
  y: number;
  size: number;
  dragging: boolean;
  zone: 'canvas' | 'map';
  mapPosition?: { lat: number; lng: number };
  isMother: boolean; // true = WASP madre, false = WASP hija
  geoZone?: GeoZone | null; // Cada WASP puede tener su propia zona
  color: string; // Color único para identificar visualmente
  assignedElementIds: string[]; // IDs de elementos asignados a esta WASP
  // Animación Galaga
  animationState?: 'spawning' | 'destroying' | 'idle';
  spawnProgress?: number; // 0-1 para animación de entrada
}

interface FlowParticle {
  id: string;
  elementId: string; // De qué elemento viene
  progress: number; // 0-1 en la curva bezier
  speed: number;
  color: string;
}

interface NetworkConnection {
  from: string; // element.id
  to: 'wasp'; // Siempre al nodo central
  active: boolean;
  traffic: number; // 0-100 intensidad
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const CONTACT_LIMITS = [50, 75, 100, 150, 300];
const PARTICLE_COUNT_PER_ELEMENT = 5;
const PARTICLE_SPEED_MIN = 0.003;
const PARTICLE_SPEED_MAX = 0.008;

const COLORS = {
  primary: '#2ca799',
  primaryDark: '#238a7f',
  primaryLight: 'rgba(44, 167, 153, 0.3)',
  dataFlow: '#3b82f6',
  favorites: '#f59e0b',
  allContacts: '#2ca799',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444'
};

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MULTI-WASP
// ═══════════════════════════════════════════════════════════════════════════

const MAX_CHILD_WASPS = 5;
const TRIPLE_CLICK_THRESHOLD = 500; // ms entre clicks para triple-click
const CHILD_WASP_SIZE = 60; // Hijas más pequeñas que madre
const MOTHER_WASP_SIZE = 80;

// ═══════════════════════════════════════════════════════════════════════════
// ANIMACIONES GALAGA
// ═══════════════════════════════════════════════════════════════════════════

const GALAGA_SPAWN_DURATION = 800; // ms para animación de entrada
const GALAGA_DESTROY_DURATION = 400; // ms para animación de destrucción
const GALAGA_SPAWN_AMPLITUDE = 50; // Amplitud del movimiento sinusoidal

// Colores únicos para cada WASP (variaciones de hue)
const WASP_COLORS = [
  '#2ca799', // Madre - Teal original
  '#3b82f6', // Hija 1 - Azul
  '#8b5cf6', // Hija 2 - Violeta
  '#ec4899', // Hija 3 - Rosa
  '#f59e0b', // Hija 4 - Naranja
  '#10b981', // Hija 5 - Verde
];

// ═══════════════════════════════════════════════════════════════════════════
// ESTILOS
// ═══════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column' as const
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#2ca799',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden', // Mantener esto para controlar scroll
    position: 'relative' as const
  },
  sidebar: {
    width: '260px',
    backgroundColor: 'white',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column' as const,
    flexShrink: 0,
    overflowY: 'auto' as const,
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)',
    zIndex: 10
  },
  sidebarSection: {
    padding: '20px 16px',
    borderBottom: '1px solid #e5e7eb'
  },
  sidebarTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    color: '#6b7280',
    marginBottom: '12px',
    letterSpacing: '0.5px'
  },
  elementItem: {
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.2s',
    userSelect: 'none' as const
  },
  // CAPA 0: Contenedor principal (base)
  splitView: {
    flex: 1,
    display: 'flex',
    gap: 0,
    overflow: 'visible', // Permitir que elementos salgan del contenedor
    position: 'relative' as const,
    zIndex: 1 // Base
  },
  // CAPA 1: Pizarra (grid background) - se ajusta cuando mapa visible
  canvas: {
    flex: 1, // Toma todo el espacio o 50% si hay mapa
    position: 'relative' as const,
    backgroundColor: 'white',
    overflow: 'hidden',
    backgroundImage: `
      linear-gradient(#e5e7eb 1px, transparent 1px),
      linear-gradient(90deg, #e5e7eb 1px, transparent 1px)
    `,
    backgroundSize: '20px 20px',
    zIndex: 2, // Encima de base
    transition: 'flex 0.3s ease'
  },
  // CAPA 2: Mapa - se ajusta al aparecer/ocultarse
  mapContainer: {
    flex: 1, // Toma 50% cuando visible
    position: 'relative' as const,
    backgroundColor: '#f3f4f6',
    zIndex: 3, // Encima de pizarra
    transition: 'flex 0.3s ease'
  },
  mapWrapper: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1 // Dentro del mapContainer
  },
  // CAPA 3: SVG Overlay (sobre pizarra y mapa)
  svgOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
    zIndex: 10 // Por encima de pizarra y mapa
  },
  // CAPA 4: Elementos drag&drop (WASP + contactos) - MÁXIMA PRIORIDAD
  elementsLayer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const, // El contenedor no captura
    zIndex: 100 // Máxima prioridad - SIEMPRE encima
  },
  btn: {
    padding: '8px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'white',
    color: '#374151'
  },
  btnPrimary: {
    backgroundColor: '#2ca799',
    color: 'white',
    border: 'none',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  btnIcon: {
    padding: '8px',
    width: '36px',
    height: '36px'
  },
  limitModal: {
    position: 'fixed' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    zIndex: 10000,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    minWidth: '400px'
  },
  limitButton: {
    padding: '12px 24px',
    margin: '8px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
    transition: 'all 0.2s'
  }
};

const responsiveStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  }

  @keyframes dataFlow {
    0% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: -20; }
  }

  @keyframes glow {
    0%, 100% { filter: drop-shadow(0 0 3px rgba(44, 167, 153, 0.5)); }
    50% { filter: drop-shadow(0 0 8px rgba(44, 167, 153, 0.8)); }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     ANIMACIONES GALAGA v3.8.0
     ═══════════════════════════════════════════════════════════════════════ */
  
  @keyframes galagaSpawn {
    0% {
      transform: translateY(-200px) scale(0.3);
      opacity: 0;
    }
    20% {
      transform: translateY(-150px) translateX(30px) scale(0.5);
      opacity: 0.5;
    }
    40% {
      transform: translateY(-100px) translateX(-30px) scale(0.7);
      opacity: 0.7;
    }
    60% {
      transform: translateY(-50px) translateX(20px) scale(0.85);
      opacity: 0.85;
    }
    80% {
      transform: translateY(-20px) translateX(-10px) scale(0.95);
      opacity: 0.95;
    }
    100% {
      transform: translateY(0) translateX(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes galagaDestroy {
    0% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
      filter: brightness(1);
    }
    20% {
      transform: scale(1.3) rotate(10deg);
      opacity: 1;
      filter: brightness(2) saturate(2);
    }
    40% {
      transform: scale(1.5) rotate(-15deg);
      opacity: 0.8;
      filter: brightness(3) saturate(0);
    }
    60% {
      transform: scale(1.2) rotate(20deg);
      opacity: 0.5;
      filter: brightness(2);
    }
    80% {
      transform: scale(0.8) rotate(-10deg);
      opacity: 0.2;
      filter: brightness(1);
    }
    100% {
      transform: scale(0) rotate(0deg);
      opacity: 0;
      filter: brightness(0);
    }
  }

  @keyframes galagaExplosionParticle {
    0% {
      transform: translate(0, 0) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(var(--px, 50px), var(--py, 50px)) scale(0);
      opacity: 0;
    }
  }

  .wasp-spawning {
    animation: galagaSpawn 0.8s ease-out forwards;
  }

  .wasp-destroying {
    animation: galagaDestroy 0.4s ease-in forwards;
  }

  .element-item:hover {
    background-color: rgba(44, 167, 153, 0.05) !important;
    border-color: #2ca799 !important;
    box-shadow: 0 2px 8px rgba(44, 167, 153, 0.15);
    transform: translateX(4px);
  }

  .btn:hover {
    background-color: #f9fafb !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }

  .btn-primary:hover {
    background-color: #238a7f !important;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15) !important;
  }

  .limit-button:hover {
    background-color: rgba(44, 167, 153, 0.1) !important;
    border-color: #2ca799 !important;
    transform: scale(1.05);
  }

  .wasp-node {
    cursor: move;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    transition: filter 0.3s;
  }

  .wasp-node:hover {
    filter: drop-shadow(0 4px 6px rgba(44, 167, 153, 0.3));
  }

  .contact-element {
    cursor: move;
    transition: all 0.2s;
  }

  .contact-element:hover {
    filter: drop-shadow(0 2px 8px rgba(44, 167, 153, 0.3));
  }
`;

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const ContactsGeoBuilder: React.FC<ContactsGeoBuilderProps> = ({
  contacts,
  selectedContactIds,
  onContactSelect,
  onSelectionComplete,
  onClose
}) => {

  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS
  // ───────────────────────────────────────────────────────────────────────

  const [showMap, setShowMap] = useState(false);
  const [elements, setElements] = useState<ContactElement[]>([]);
  const [waspNode, setWaspNode] = useState<WASPNode>({
    x: 400,
    y: 300,
    size: 80,
    dragging: false,
    zone: 'canvas' // Inicia en canvas
  });
  const [geoZone, setGeoZone] = useState<GeoZone | null>(null);
  const [drawingMode, setDrawingMode] = useState<'circle' | 'polygon' | null>(null);
  const [particles, setParticles] = useState<FlowParticle[]>([]);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalElement, setLimitModalElement] = useState<ContactElement | null>(null);
  const [draggedElement, setDraggedElement] = useState<ContactElement | null>(null);
  const [draggingElementId, setDraggingElementId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{x: number, y: number}>({ x: 0, y: 0 });
  const [finalized, setFinalized] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [zoneWarning, setZoneWarning] = useState<string | null>(null);
  const [validZoneWaspIds, setValidZoneWaspIds] = useState<string[]>([]); // WASPs con zonas válidas (verde + check)

  // ═══════════════════════════════════════════════════════════════════════
  // ESTADOS MULTI-WASP v3.7.0
  // ═══════════════════════════════════════════════════════════════════════
  
  const [wasps, setWasps] = useState<WASPInstance[]>([
    {
      id: 'mother-wasp',
      x: 400,
      y: 300,
      size: MOTHER_WASP_SIZE,
      dragging: false,
      zone: 'canvas',
      isMother: true,
      geoZone: null,
      color: WASP_COLORS[0],
      assignedElementIds: [], // Madre empieza con todos los elementos por defecto
      animationState: 'idle'
    }
  ]);
  const [activeWaspId, setActiveWaspId] = useState<string>('mother-wasp');
  const [geomanReady, setGeomanReady] = useState(false);

  
  // Refs para triple-click detection
  const clickCountRef = useRef<number>(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastClickedWaspRef = useRef<string | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();

  // ───────────────────────────────────────────────────────────────────────
  // COMPUTED: CONTACTOS DINÁMICOS
  // ───────────────────────────────────────────────────────────────────────

  /*const favoritesContacts = useMemo(() => {
    return contacts.filter(c => c.tags.includes('vip'));
  }, [contacts]);*/

  const favoritesContacts = useMemo(() => {
  const realFavorites = contacts.filter(c => c.tags.includes('vip'));
  
  // MOCK TEMPORAL - solo agregar si no existe ya
  const matildeExists = realFavorites.some(c => c.phone === '+56981925986');
  
  if (matildeExists) {
    return realFavorites;
  }
  
  const mockMatilde: ContactDB = {
    id: 'mock-matilde-temp',
    name: 'Matilde Villarroel',
    phone: '+56981925986',
    tags: ['vip'],
    groups: [],
    status: 'active',
    source: 'manual',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    location: {
      lat: -33.6986657,
      lng: -71.0854323,
      source: 'manual'
    }
  };
  
  return [...realFavorites, mockMatilde];
}, [contacts]);

  const allContactsWithLimit = useCallback((limit: number) => {
    return contacts.slice(0, Math.min(limit, 300));
  }, [contacts]);

  // ───────────────────────────────────────────────────────────────────────
  // LEAFLET INITIALIZATION
  // ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!showMap) return;

    if (typeof window === 'undefined' || !(window as any).L) {
      console.error('⚠️ Leaflet not loaded');
      return;
    }

    const L = (window as any).L;

    if (!mapRef.current) {
      const map = L.map('geomap-canvas', {
        center: [-33.4489, -70.6693],
        zoom: 12,
        zoomControl: true,
        preferCanvas: true // Better performance
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);

      mapRef.current = map;

      // Renderizar elementos en mapa
      renderMapElements();
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [showMap]);

  // ───────────────────────────────────────────────────────────────────────
  // LEAFLET.PM DYNAMIC LOAD
  // ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
  if (!showMap) return;

  // Cargar Leaflet.PM dinámicamente
  const loadLeafletPM = () => {
    if ((window as any).L?.PM) {
      console.log('✅ Leaflet.PM already loaded');
      setGeomanReady(true); // ⬅️ AGREGAR ESTA LÍNEA
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      // CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/@geoman-io/leaflet-geoman-free@latest/dist/leaflet-geoman.css';
      document.head.appendChild(link);

      // JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@geoman-io/leaflet-geoman-free@latest/dist/leaflet-geoman.min.js';
      script.onload = () => {
        console.log('✅ Leaflet.PM loaded successfully');
        setGeomanReady(true); // ⬅️ AGREGAR ESTA LÍNEA
        resolve();
      };
      script.onerror = () => {
        console.error('❌ Failed to load Leaflet.PM');
        reject();
      };
      document.head.appendChild(script);
    });
  };

  loadLeafletPM();
}, [showMap]);

  // ───────────────────────────────────────────────────────────────────────
  // VALIDACION DE ZONA
  // ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const newValidWaspIds: string[] = [];
    
    // Validar CADA WASP con SU propia zona
    wasps.forEach(wasp => {
      if (wasp.zone !== 'map' || !wasp.geoZone || !wasp.geoZone.layer) {
        return;
      }

      // Verificar si esta WASP está dentro de SU zona
      if (!wasp.mapPosition) {
        // Sin posición de mapa, mostrar naranja
        wasp.geoZone.layer.setStyle({ 
          fillColor: '#f59e0b', 
          color: '#f59e0b', 
          fillOpacity: 0.2 
        });
        return;
      }

      const waspInsideZone = wasp.geoZone.type === 'circle' 
        ? isPointInCircle(wasp.mapPosition, wasp.geoZone.coordinates[0], wasp.geoZone.radius!)
        : isPointInPolygon(wasp.mapPosition, wasp.geoZone.coordinates);

      if (!waspInsideZone) {
        // WASP fuera de su zona → naranja
        wasp.geoZone.layer.setStyle({ 
          fillColor: '#f59e0b', 
          color: '#f59e0b', 
          fillOpacity: 0.2 
        });
        
        // Solo mostrar warning si es la WASP activa
        if (wasp.id === activeWaspId) {
          setZoneWarning('⚠️ Posiciona la avispa dentro de la zona para activarla');
        }
      } else {
        // WASP dentro → verificar contactos de SUS elementos asignados
        const waspElementContacts: ContactDB[] = [];
        wasp.assignedElementIds.forEach(elId => {
          const element = elements.find(e => e.id === elId);
          if (element) {
            element.contactIds.forEach(id => {
              let contact = contacts.find(c => c.id === id);
              if (!contact) {
                contact = favoritesContacts.find(c => c.id === id);
              }
              if (contact && !waspElementContacts.find(c => c.id === id)) {
                waspElementContacts.push(contact);
              }
            });
          }
        });

        const contactsInZone = filterContactsByGeoZone(waspElementContacts, wasp.geoZone);
      
        if (contactsInZone.length === 0) {
          // Sin contactos en zona → rojo
          wasp.geoZone.layer.setStyle({ 
            fillColor: '#ef4444', 
            color: '#ef4444', 
            fillOpacity: 0.3 
          });
          
          if (wasp.id === activeWaspId) {
            setZoneWarning('⚠️ Ningún contacto de esta WASP tiene ubicación en su zona');
          }
        } else {
          // Todo OK → VERDE + marcar como válida
          wasp.geoZone.layer.setStyle({ 
            fillColor: '#10b981', 
            color: '#10b981', 
            fillOpacity: 0.25 
          });
          
          newValidWaspIds.push(wasp.id);
          
          if (wasp.id === activeWaspId) {
            setZoneWarning(null);
          }
          
          console.log(`✅ WASP ${wasp.id}: ${contactsInZone.length} contactos en zona`);
        }
      }
    });

    // Actualizar lista de WASPs con zonas válidas
    setValidZoneWaspIds(newValidWaspIds);

    // Limpiar warning si no hay WASPs en mapa con zonas
    const waspsInMapWithZones = wasps.filter(w => w.zone === 'map' && w.geoZone);
    if (waspsInMapWithZones.length === 0) {
      setZoneWarning(null);
    }
  }, [wasps, activeWaspId, elements, contacts, favoritesContacts]);

  // ───────────────────────────────────────────────────────────────────────
  // GEOMAN CONTROLS SETUP
  // ───────────────────────────────────────────────────────────────────────

useEffect(() => {
  // Verificar si alguna WASP está en el mapa
  const anyWaspInMap = wasps.some(w => w.zone === 'map');
  if (!mapRef.current || !anyWaspInMap) return;

  const L = (window as any).L;
  if (!L?.PM || !geomanReady || !mapRef.current.pm) { // ⬅️ AGREGAR !geomanReady
    console.warn('⚠️ Geoman not loaded yet');
    return;
  }

  // Activar controles
  mapRef.current.pm.addControls({
    position: 'topleft',
    drawCircle: true,
    drawPolygon: true,
    drawPolyline: false,
    drawMarker: false,
    drawRectangle: true,
    drawCircleMarker: false,
    editMode: true,
    dragMode: false,
    cutPolygon: false,
    removalMode: true
  });

  // Handler cuando se crea geometría - MULTI-WASP v3.7.0
  const handleCreate = (e: any) => {
    const layer = e.layer;
    const shape = e.shape;

    // Obtener WASP activa actual
    const currentActiveWaspId = activeWaspId;
    const currentActiveWasp = wasps.find(w => w.id === currentActiveWaspId);

    // Solo limpiar zona anterior de la WASP activa (si tiene una)
    // NO tocar zonas de otras WASPs
    if (currentActiveWasp?.geoZone?.layer) {
      currentActiveWasp.geoZone.layer.remove();
    }

    let newZone: GeoZone | null = null;

    if (shape === 'Circle') {
      const center = layer.getLatLng();
      const radius = layer.getRadius();

      if (radius > 50000) {
        alert('⚠️ Radio máximo: 50km');
        layer.remove();
        return;
      }

      newZone = {
        type: 'circle',
        coordinates: [{ lat: center.lat, lng: center.lng }],
        radius: radius,
        layer: layer
      };

      console.log(`🔵 Círculo para WASP ${currentActiveWaspId}: ${Math.round(radius)}m`);
    } 
    else if (shape === 'Polygon' || shape === 'Rectangle') {
      const latlngs = layer.getLatLngs()[0];
      const coords = latlngs.map((ll: any) => ({ lat: ll.lat, lng: ll.lng }));

      if (coords.length > 1000) {
        alert('⚠️ Máximo 1000 vértices');
        layer.remove();
        return;
      }

      newZone = {
        type: 'polygon',
        coordinates: coords,
        layer: layer
      };

      console.log(`🔶 Polígono para WASP ${currentActiveWaspId}: ${coords.length} vértices`);
    }

    // Actualizar zona SOLO en la WASP activa (MULTI-WASP)
    if (newZone) {
      setWasps(prev => prev.map(w => 
        w.id === currentActiveWaspId 
          ? { ...w, geoZone: newZone }
          : w
      ));

      // Actualizar geoZone global solo si es la madre (backward compatibility)
      if (currentActiveWasp?.isMother) {
        setGeoZone(newZone);
      }
    }

    setDrawingMode(null);
  };

  mapRef.current.on('pm:create', handleCreate);

  return () => {
    if (mapRef.current) {
      mapRef.current.pm.removeControls();
      mapRef.current.off('pm:create', handleCreate);
    }
  };
}, [wasps, activeWaspId, elements, geomanReady]); // ✅ Dependencias actualizadas

  // ───────────────────────────────────────────────────────────────────────
  // ANIMATION LOOP (Partículas)
  // ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const animate = () => {
      setParticles(prev => prev.map(p => ({
        ...p,
        progress: (p.progress + p.speed) % 1
      })));

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (elements.filter(e => e.zone === 'canvas').length > 0) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [elements]);

  // ───────────────────────────────────────────────────────────────────────
  // CREAR PARTÍCULAS PARA ELEMENTO
  // ───────────────────────────────────────────────────────────────────────

  const createParticlesForElement = useCallback((elementId: string, color: string) => {
    const newParticles: FlowParticle[] = [];
    for (let i = 0; i < PARTICLE_COUNT_PER_ELEMENT; i++) {
      newParticles.push({
        id: `${elementId}-particle-${i}`,
        elementId,
        progress: i / PARTICLE_COUNT_PER_ELEMENT,
        speed: PARTICLE_SPEED_MIN + Math.random() * (PARTICLE_SPEED_MAX - PARTICLE_SPEED_MIN),
        color
      });
    }
    setParticles(prev => [...prev.filter(p => p.elementId !== elementId), ...newParticles]);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // MULTI-WASP HANDLERS v3.8.0 - Con animaciones Galaga
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Spawn una WASP hija desde la madre
   * Se activa con triple-click en la WASP madre
   * SOLO funciona cuando la madre está en el MAPA
   */
  const spawnChildWASP = useCallback(() => {
    const mother = wasps.find(w => w.isMother);
    if (!mother) return;

    // Solo permitir spawn en modo mapa
    if (mother.zone !== 'map') {
      console.warn('⚠️ Multi-WASP solo disponible en modo mapa');
      return;
    }

    const childCount = wasps.filter(w => !w.isMother).length;
    
    if (childCount >= MAX_CHILD_WASPS) {
      console.warn(`⚠️ Máximo ${MAX_CHILD_WASPS} WASPs hijas alcanzado`);
      return;
    }

    const newChildId = `child-wasp-${Date.now()}`;
    const colorIndex = childCount + 1;
    
    // Posición con offset desde la madre
    const offsetX = 100 + (childCount * 40);
    const offsetY = 60 + (childCount * 30);

    const newChild: WASPInstance = {
      id: newChildId,
      x: mother.x + offsetX,
      y: mother.y + offsetY,
      size: CHILD_WASP_SIZE,
      dragging: false,
      zone: 'map',
      mapPosition: mother.mapPosition ? {
        lat: mother.mapPosition.lat + 0.008 * (childCount + 1),
        lng: mother.mapPosition.lng + 0.006 * (childCount + 1)
      } : undefined,
      isMother: false,
      geoZone: null,
      color: WASP_COLORS[colorIndex] || WASP_COLORS[1],
      assignedElementIds: [],
      animationState: 'spawning' // Iniciar con animación Galaga
    };

    setWasps(prev => [...prev, newChild]);
    setActiveWaspId(newChildId);

    // Terminar animación después del tiempo
    setTimeout(() => {
      setWasps(prev => prev.map(w => 
        w.id === newChildId ? { ...w, animationState: 'idle' } : w
      ));
    }, GALAGA_SPAWN_DURATION);

    console.log(`🐝 WASP hija #${childCount + 1} spawned con animación Galaga:`, newChildId);
  }, [wasps]);

  /**
   * Eliminar una WASP hija (triple-click en hija)
   * Incluye animación de destrucción Galaga
   */
  const removeChildWASP = useCallback((waspId: string) => {
    const wasp = wasps.find(w => w.id === waspId);
    
    if (!wasp || wasp.isMother) {
      console.warn('⚠️ No se puede eliminar la WASP madre');
      return;
    }

    // Iniciar animación de destrucción
    setWasps(prev => prev.map(w => 
      w.id === waspId ? { ...w, animationState: 'destroying' } : w
    ));

    // Después de la animación, eliminar y reasignar elementos a madre
    setTimeout(() => {
      // Reasignar elementos de la hija a la madre
      const childElements = wasp.assignedElementIds;
      
      setWasps(prev => {
        const filtered = prev.filter(w => w.id !== waspId);
        // Asignar elementos huérfanos a la madre
        return filtered.map(w => 
          w.isMother 
            ? { ...w, assignedElementIds: [...w.assignedElementIds, ...childElements] }
            : w
        );
      });

      // Limpiar zona geográfica si existe
      if (wasp.geoZone?.layer) {
        wasp.geoZone.layer.remove();
      }

      setActiveWaspId('mother-wasp');
      console.log(`💥 WASP hija destruida estilo Galaga:`, waspId);
    }, GALAGA_DESTROY_DURATION);
  }, [wasps]);

  /**
   * Destruir TODAS las hijas cuando madre vuelve a canvas
   * Animación Galaga en cadena
   */
  const destroyAllChildWasps = useCallback(() => {
    const children = wasps.filter(w => !w.isMother);
    if (children.length === 0) return;

    // Iniciar animación de destrucción en todas las hijas
    setWasps(prev => prev.map(w => 
      !w.isMother ? { ...w, animationState: 'destroying' } : w
    ));

    // Después de la animación, eliminar todas y reasignar elementos a madre
    setTimeout(() => {
      const allChildElements = children.flatMap(c => c.assignedElementIds);
      
      // Limpiar zonas geográficas
      children.forEach(child => {
        if (child.geoZone?.layer) {
          child.geoZone.layer.remove();
        }
      });

      setWasps(prev => {
        const mother = prev.find(w => w.isMother);
        if (!mother) return prev;
        
        return [{
          ...mother,
          assignedElementIds: [...mother.assignedElementIds, ...allChildElements]
        }];
      });

      setActiveWaspId('mother-wasp');
      console.log(`💥💥💥 Todas las hijas destruidas - Modo pizarra activado`);
    }, GALAGA_DESTROY_DURATION);
  }, [wasps]);

  /**
   * Handler de click en WASP - detecta triple-click
   */
  const handleWaspClick = useCallback((waspId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (finalized) return;

    // Siempre seleccionar la WASP clickeada como activa
    setActiveWaspId(waspId);

    // Verificar si es el mismo WASP que el click anterior
    if (lastClickedWaspRef.current !== waspId) {
      clickCountRef.current = 0;
      lastClickedWaspRef.current = waspId;
    }

    // Incrementar contador de clicks
    clickCountRef.current += 1;

    // Limpiar timer anterior
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    // Si llegamos a 3 clicks
    if (clickCountRef.current >= 3) {
      const wasp = wasps.find(w => w.id === waspId);
      
      if (wasp?.isMother) {
        // Triple-click en madre → Spawn hija
        spawnChildWASP();
      } else {
        // Triple-click en hija → Eliminar hija
        removeChildWASP(waspId);
      }
      
      clickCountRef.current = 0;
      lastClickedWaspRef.current = null;
      return;
    }

    // Resetear contador después del threshold
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
      lastClickedWaspRef.current = null;
    }, TRIPLE_CLICK_THRESHOLD);
  }, [wasps, finalized, spawnChildWASP, removeChildWASP]);

  /**
   * Obtener la WASP activa actual
   */
  const activeWasp = useMemo(() => {
    return wasps.find(w => w.id === activeWaspId) || wasps[0];
  }, [wasps, activeWaspId]);

  /**
   * Obtener la WASP madre
   */
  const motherWasp = useMemo(() => {
    return wasps.find(w => w.isMother) || wasps[0];
  }, [wasps]);

  /**
   * Contar WASPs con zonas dibujadas
   */
  const waspsWithZones = useMemo(() => {
    return wasps.filter(w => w.geoZone !== null && w.geoZone !== undefined);
  }, [wasps]);

  // ═══════════════════════════════════════════════════════════════════════
  // ASIGNACIÓN DE ELEMENTOS A WASPS v3.7.0
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Encuentra la WASP más cercana a una posición dada
   * @param x Posición X en píxeles
   * @param y Posición Y en píxeles
   * @param maxDistance Distancia máxima para considerar "cerca" (default 150px)
   * @returns WASP más cercana o null si ninguna está cerca
   */
  const findNearestWasp = useCallback((x: number, y: number, maxDistance: number = 150): WASPInstance | null => {
    let nearest: WASPInstance | null = null;
    let minDistance = maxDistance;

    wasps.forEach(wasp => {
      const distance = Math.sqrt(Math.pow(wasp.x - x, 2) + Math.pow(wasp.y - y, 2));
      if (distance < minDistance) {
        minDistance = distance;
        nearest = wasp;
      }
    });

    return nearest;
  }, [wasps]);

  /**
   * Asigna un elemento a una WASP específica
   * @param elementId ID del elemento a asignar
   * @param waspId ID de la WASP destino
   */
  const assignElementToWasp = useCallback((elementId: string, waspId: string) => {
    setWasps(prev => prev.map(wasp => {
      if (wasp.id === waspId) {
        // Agregar elemento a esta WASP si no está ya
        if (!wasp.assignedElementIds.includes(elementId)) {
          console.log(`🔗 Elemento ${elementId} asignado a WASP ${waspId}`);
          return {
            ...wasp,
            assignedElementIds: [...wasp.assignedElementIds, elementId]
          };
        }
      } else {
        // Remover elemento de otras WASPs
        return {
          ...wasp,
          assignedElementIds: wasp.assignedElementIds.filter(id => id !== elementId)
        };
      }
      return wasp;
    }));
  }, []);

  /**
   * Desasigna un elemento de todas las WASPs
   * @param elementId ID del elemento a desasignar
   */
  const unassignElement = useCallback((elementId: string) => {
    setWasps(prev => prev.map(wasp => ({
      ...wasp,
      assignedElementIds: wasp.assignedElementIds.filter(id => id !== elementId)
    })));
  }, []);

  /**
   * Obtener la WASP a la que pertenece un elemento
   * @param elementId ID del elemento
   * @returns WASP dueña o null
   */
  const getElementOwnerWasp = useCallback((elementId: string): WASPInstance | null => {
    return wasps.find(wasp => wasp.assignedElementIds.includes(elementId)) || null;
  }, [wasps]);

  // ───────────────────────────────────────────────────────────────────────
  // DRAG & DROP HANDLERS
  // ───────────────────────────────────────────────────────────────────────

  const handleDragStartElement = (e: React.DragEvent, type: 'favorites' | 'all-contacts') => {
    if (finalized) {
      e.preventDefault();
      return;
    }

    const contactIds = type === 'favorites' 
      ? favoritesContacts.map(c => c.id)
      : allContactsWithLimit(50).map(c => c.id);

    const newElement: ContactElement = {
      id: `${type}-${Date.now()}`,
      type,
      label: type === 'favorites' ? 'Favoritos' : 'Todos los Contactos',
      icon: type === 'favorites' ? '⭐' : '👥',
      position: { x: 0, y: 0 },
      zone: 'canvas',
      limit: type === 'all-contacts' ? 50 : undefined,
      contactIds,
      color: type === 'favorites' ? COLORS.favorites : COLORS.allContacts,
      locked: false
    };

    setDraggedElement(newElement);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', newElement.id);
  };

  const handleDropCanvas = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedElement || !canvasRef.current || finalized) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const element: ContactElement = {
      ...draggedElement,
      position: { x, y },
      zone: 'canvas'
    };

    setElements(prev => [...prev, element]);
    createParticlesForElement(element.id, element.color);
    setDraggedElement(null);

    // En canvas, siempre asignar a WASP madre
    const mother = wasps.find(w => w.isMother);
    if (mother) {
      assignElementToWasp(element.id, mother.id);
    }

    console.log(`✅ Element dropped on CANVAS → asignado a madre:`, element);
  };

  const handleDropMap = (e: React.DragEvent) => {
  e.preventDefault();
  
  if (!draggedElement || !mapRef.current || finalized) return;

  const L = (window as any).L;
  if (!L) return;

  // NO convertir a lat/lng - mantener en píxeles
  const rect = canvasRef.current!.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const element: ContactElement = {
    ...draggedElement,
    position: { x, y }, // Píxeles, NO lat/lng
    zone: 'map'
  };

  setElements(prev => [...prev, element]);
  setDraggedElement(null);
  createParticlesForElement(element.id, element.color);
  renderMapElement(element);

  // En mapa, buscar WASP más cercana para asignar
  const nearestWasp = findNearestWasp(x, y);
  if (nearestWasp) {
    assignElementToWasp(element.id, nearestWasp.id);
    console.log(`✅ Element dropped on MAP → asignado a ${nearestWasp.isMother ? 'madre' : 'hija'} (${nearestWasp.id}):`, element);
  } else {
    // Si no hay WASP cerca, asignar a madre por defecto
    const mother = wasps.find(w => w.isMother);
    if (mother) {
      assignElementToWasp(element.id, mother.id);
      console.log(`✅ Element dropped on MAP → asignado a madre (default):`, element);
    }
  }
};

  // ───────────────────────────────────────────────────────────────────────
  // MOVE ELEMENT (Canvas → Map, Map → Canvas)
  // ───────────────────────────────────────────────────────────────────────

  const moveElementToMap = (elementId: string) => {
    if (finalized || !mapRef.current) return;

    const element = elements.find(e => e.id === elementId);
    if (!element || element.zone === 'map') return;

    const L = (window as any).L;
    if (!L) return;

    // Posición central del mapa
    const center = mapRef.current.getCenter();
    
    const updatedElement: ContactElement = {
      ...element,
      position: { x: center.lat, y: center.lng },
      zone: 'map'
    };

    setElements(prev => prev.map(e => e.id === elementId ? updatedElement : e));
    setParticles(prev => prev.filter(p => p.elementId !== elementId));
    
    renderMapElement(updatedElement);
  };

  const moveElementToCanvas = (elementId: string) => {
    if (finalized) return;

    const element = elements.find(e => e.id === elementId);
    if (!element || element.zone === 'canvas') return;

    const updatedElement: ContactElement = {
      ...element,
      position: { x: 300, y: 300 },
      zone: 'canvas'
    };

    setElements(prev => prev.map(e => e.id === elementId ? updatedElement : e));
    createParticlesForElement(updatedElement.id, updatedElement.color);

    // Remover del mapa
    removeMapElement(elementId);
  };

  // Mover WASP de vuelta al canvas
  const moveWASPToCanvas = () => {
    if (waspNode.zone === 'canvas' || finalized) return;

    // Remover marcador del mapa
    if ((waspNode as any).mapMarker) {
      (waspNode as any).mapMarker.remove();
    }

    // Limpiar zona dibujada
    if (geoZone?.layer) {
      geoZone.layer.remove();
    }

    setWaspNode({
      x: 400,
      y: 300,
      size: 80,
      dragging: false,
      zone: 'canvas'
    });

    setGeoZone(null);
    setDrawingMode(null);

    console.log('⬅️ WASP moved back to CANVAS');
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER MAP ELEMENTS
  // ───────────────────────────────────────────────────────────────────────

  const renderMapElements = useCallback(() => {
    if (!mapRef.current) return;

    const mapElements = elements.filter(e => e.zone === 'map');
    mapElements.forEach(renderMapElement);
  }, [elements]);

  const renderMapElement = (element: ContactElement) => {
    if (!mapRef.current) return;

    const L = (window as any).L;
    if (!L) return;

    const elementContacts = contacts.filter(c => element.contactIds.includes(c.id));
    const contactsWithLocation = elementContacts.filter(c => c.location && c.location.lat && c.location.lng);

    if (contactsWithLocation.length === 0) {
      console.warn(`⚠️ No contacts with location for element ${element.id}`);
      return;
    }

    // Calcular bounds
    const latlngs = contactsWithLocation.map(c => [c.location!.lat, c.location!.lng]);
    
    // Crear polígono convex hull (simplificado)
    const polygon = L.polygon(latlngs, {
      color: element.color,
      fillColor: element.color,
      fillOpacity: 0.2,
      weight: 2,
      className: `element-polygon-${element.id}`
    });

    polygon.bindPopup(`
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #374151; padding: 8px;">
        <strong style="color: ${element.color};">${element.icon} ${element.label}</strong><br/>
        <span style="color: #6b7280;">Contactos: <strong>${element.contactIds.length}</strong></span><br/>
        <span style="font-size: 0.8em; color: #2ca799;">Click para ver red compleja</span>
      </div>
    `);

    polygon.on('click', () => {
      showComplexNetwork(element);
    });

    polygon.addTo(mapRef.current);

    // Agregar marcadores de nodos
    contactsWithLocation.forEach(contact => {
      const marker = L.circleMarker([contact.location!.lat, contact.location!.lng], {
        radius: 4,
        fillColor: element.color,
        color: '#fff',
        weight: 1,
        opacity: 0.8,
        fillOpacity: 0.6,
        className: `element-marker-${element.id}`
      });

      marker.bindTooltip(`
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 0.75em;">
          <strong style="color: #374151;">${contact.name}</strong><br/>
          <span style="color: #6b7280;">📞 ${contact.phone}</span>
        </div>
      `, {
        permanent: false,
        direction: 'top'
      });

      marker.addTo(mapRef.current);
    });
  };

  const removeMapElement = (elementId: string) => {
    if (!mapRef.current) return;

    // Remover polígonos y marcadores del elemento
    mapRef.current.eachLayer((layer: any) => {
      if (layer.options && 
          (layer.options.className?.includes(`element-polygon-${elementId}`) ||
           layer.options.className?.includes(`element-marker-${elementId}`))) {
        mapRef.current.remove(layer);
      }
    });
  };

  const showComplexNetwork = (element: ContactElement) => {
    console.log(`🔍 Showing complex network for ${element.label}`);
    // TODO: Implementar visualización de grafo de red compleja
    alert(`RED COMPLEJA: ${element.label}\n${element.contactIds.length} nodos conectados\n\n(Implementación de grafo en próxima iteración)`);
  };

  // ───────────────────────────────────────────────────────────────────────
  // LIMIT MODAL
  // ───────────────────────────────────────────────────────────────────────

  const handleRightClick = (e: React.MouseEvent, element: ContactElement) => {
    e.preventDefault();
    
    if (finalized || element.type !== 'all-contacts') return;

    setLimitModalElement(element);
    setShowLimitModal(true);
  };

  const handleLimitChange = (newLimit: number) => {
    if (!limitModalElement) return;

    const updatedContactIds = allContactsWithLimit(newLimit).map(c => c.id);

    setElements(prev => prev.map(e => 
      e.id === limitModalElement.id 
        ? { ...e, limit: newLimit, contactIds: updatedContactIds }
        : e
    ));

    setShowLimitModal(false);
    setLimitModalElement(null);

    // Actualizar mapa si está en mapa
    if (limitModalElement.zone === 'map') {
      removeMapElement(limitModalElement.id);
      setTimeout(() => {
        const updated = elements.find(el => el.id === limitModalElement.id);
        if (updated) renderMapElement(updated);
      }, 100);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // DRAG EXISTING ELEMENTS (después de soltar)
  // ───────────────────────────────────────────────────────────────────────

  const handleElementMouseDown = (e: React.MouseEvent, elementId: string) => {
  if (finalized) return;
  
  const element = elements.find(el => el.id === elementId);
  if (!element) return;

  const rect = canvasRef.current!.getBoundingClientRect();
  const offsetX = e.clientX - rect.left - element.position.x;
  const offsetY = e.clientY - rect.top - element.position.y;

  setDraggingElementId(elementId);
  setDragOffset({ x: offsetX, y: offsetY });
};

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingElementId || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - dragOffset.x;
      const y = e.clientY - rect.top - dragOffset.y;

      setElements(prev => prev.map(el => 
        el.id === draggingElementId 
          ? { ...el, position: { x, y } }
          : el
      ));
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!draggingElementId || !canvasRef.current) {
        setDraggingElementId(null);
        return;
      }

      // Obtener posición final del elemento
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - dragOffset.x;
      const y = e.clientY - rect.top - dragOffset.y;

      // Buscar WASP más cercana para reasignar
      const nearestWasp = findNearestWasp(x, y);
      if (nearestWasp) {
        assignElementToWasp(draggingElementId, nearestWasp.id);
        console.log(`🔄 Elemento ${draggingElementId} reasignado a WASP ${nearestWasp.id}`);
      }

      setDraggingElementId(null);
    };

    if (draggingElementId) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingElementId, dragOffset, findNearestWasp, assignElementToWasp]);

  // ───────────────────────────────────────────────────────────────────────
  // FINALIZAR SELECCIÓN
  // ───────────────────────────────────────────────────────────────────────

  const handleFinalize = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmFinalize = () => {
    // ═══════════════════════════════════════════════════════════════════════
    // LÓGICA MULTI-WASP v3.7.0 - OR entre zonas
    // ═══════════════════════════════════════════════════════════════════════
    
    let finalContacts = new Set<string>();
    let finalContactObjects: ContactDB[] = [];
    
    // Obtener todos los contactos de los elementos
    const allElementContacts: ContactDB[] = [];
    elements.forEach(el => {
      el.contactIds.forEach(id => {
        let contact = contacts.find(c => c.id === id);
        if (!contact) {
          contact = favoritesContacts.find(c => c.id === id);
        }
        if (contact && !allElementContacts.find(c => c.id === id)) {
          allElementContacts.push(contact);
        }
      });
    });

    // Verificar si hay WASPs en modo mapa con zonas
    const waspsInMapWithZones = wasps.filter(w => w.zone === 'map' && w.geoZone);
    
    if (waspsInMapWithZones.length > 0) {
      // ═══════════════════════════════════════════════════════════════════
      // MODO GEOMARKETING MULTI-ZONA (OR lógico) - Solo elementos asignados
      // ═══════════════════════════════════════════════════════════════════
      
      console.log(`🎯 GEOMARKETING MULTI-ZONA: ${waspsInMapWithZones.length} zonas activas`);
      
      // OR lógico: Contacto en zona de WASP con elementos asignados a ella
      waspsInMapWithZones.forEach(wasp => {
        if (wasp.geoZone) {
          // Solo contactos de elementos ASIGNADOS a esta WASP
          const waspElementContacts: ContactDB[] = [];
          wasp.assignedElementIds.forEach(elId => {
            const element = elements.find(e => e.id === elId);
            if (element) {
              element.contactIds.forEach(id => {
                let contact = contacts.find(c => c.id === id);
                if (!contact) {
                  contact = favoritesContacts.find(c => c.id === id);
                }
                if (contact && !waspElementContacts.find(c => c.id === id)) {
                  waspElementContacts.push(contact);
                }
              });
            }
          });
          
          const contactsInThisZone = filterContactsByGeoZone(waspElementContacts, wasp.geoZone);
          contactsInThisZone.forEach(c => {
            if (!finalContacts.has(c.id)) {
              finalContacts.add(c.id);
              finalContactObjects.push(c);
            }
          });
          console.log(`   🐝 WASP ${wasp.id}: ${wasp.assignedElementIds.length} elementos, ${waspElementContacts.length} contactos → ${contactsInThisZone.length} en zona`);
        }
      });
      
      console.log(`   📊 Total único (OR): ${finalContacts.size} contactos`);
      
    } else if (waspNode.zone === 'map' && geoZone && waspNode.mapPosition) {
      // ═══════════════════════════════════════════════════════════════════
      // BACKWARD COMPATIBILITY: Modo con zona global (geoZone)
      // ═══════════════════════════════════════════════════════════════════
      
      const contactsInZone = filterContactsByGeoZone(allElementContacts, geoZone);
      contactsInZone.forEach(c => {
        finalContacts.add(c.id);
        finalContactObjects.push(c);
      });
      
      console.log(`🎯 GEOMARKETING (zona global): ${finalContacts.size} contactos finales`);
      
    } else {
      // ═══════════════════════════════════════════════════════════════════
      // MODO MASIVO (sin filtro geográfico)
      // ═══════════════════════════════════════════════════════════════════
      
      allElementContacts.forEach(contact => {
        if (!finalContacts.has(contact.id)) {
          finalContacts.add(contact.id);
          finalContactObjects.push(contact);
        }
      });
      
      console.log(`📡 MODO MASIVO: ${finalContacts.size} contactos finales`);
    }

    setFinalized(true);
    setElements(prev => prev.map(e => ({ ...e, locked: true })));
    setShowConfirmModal(false);
    
    // Auto-seleccionar contactos (objetos completos para soportar mocks)
    finalContactObjects.forEach(contact => {
      if (!selectedContactIds.includes(contact.id)) {
        onContactSelect(contact);
      }
    });
    
    console.log(`✅ ${finalContacts.size} contactos auto-seleccionados (incluyendo mocks)`);
    
    onSelectionComplete(finalContactObjects); 
  };

  // ───────────────────────────────────────────────────────────────────────
  // BEZIER CURVE CALCULATION
  // ───────────────────────────────────────────────────────────────────────

  const getBezierPoint = (t: number, start: {x: number, y: number}, end: {x: number, y: number}) => {
    const control1 = {
      x: start.x + (end.x - start.x) * 0.3,
      y: start.y - 100
    };
    const control2 = {
      x: start.x + (end.x - start.x) * 0.7,
      y: end.y - 100
    };

    const mt = 1 - t;
    const x = mt * mt * mt * start.x + 
              3 * mt * mt * t * control1.x + 
              3 * mt * t * t * control2.x + 
              t * t * t * end.x;
    const y = mt * mt * mt * start.y + 
              3 * mt * mt * t * control1.y + 
              3 * mt * t * t * control2.y + 
              t * t * t * end.y;

    return { x, y };
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: SIDEBAR
  // ───────────────────────────────────────────────────────────────────────

  const renderSidebar = () => (
    <div style={styles.sidebar}>
      <div style={styles.sidebarSection}>
        <h6 style={styles.sidebarTitle}>Elementos Dinámicos</h6>
        
        {/* Favoritos */}
        <div
          className="element-item"
          draggable={!finalized}
          onDragStart={(e) => handleDragStartElement(e, 'favorites')}
          style={{
            ...styles.elementItem,
            opacity: finalized ? 0.5 : 1,
            cursor: finalized ? 'not-allowed' : 'grab'
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>⭐</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.875rem' }}>Favoritos</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
              {favoritesContacts.length} contactos VIP
            </div>
          </div>
        </div>

        {/* Todos los Contactos */}
        <div
          className="element-item"
          draggable={!finalized}
          onDragStart={(e) => handleDragStartElement(e, 'all-contacts')}
          style={{
            ...styles.elementItem,
            opacity: finalized ? 0.5 : 1,
            cursor: finalized ? 'not-allowed' : 'grab'
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>👥</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#2ca799', fontWeight: 600, fontSize: '0.875rem' }}>Todos los Contactos</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
              Default: 50 (ajustable)
            </div>
          </div>
        </div>
      </div>

      <div style={styles.sidebarSection}>
        <h6 style={styles.sidebarTitle}>Vista</h6>
        <button
          style={{ ...styles.btn, ...styles.btnPrimary, width: '100%', justifyContent: 'center' }}
          onClick={() => setShowMap(!showMap)}
        >
          <FontAwesomeIcon icon={faMapMarkedAlt} />
          {showMap ? 'Ocultar Mapa' : 'Mostrar Mapa'}
        </button>
      </div>

      {/* Herramientas Geomarketing - Solo cuando alguna WASP en mapa */}
      {wasps.some(w => w.zone === 'map') && (
        <div style={styles.sidebarSection}>
          <h6 style={styles.sidebarTitle}>🎯 Herramientas Geo</h6>
          
          {/* MULTI-WASP: Selector de WASPs v3.7.0 */}
          {wasps.length > 1 && (
            <div style={{
              marginBottom: '12px',
              padding: '8px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #10b981'
            }}>
              <div style={{ fontSize: '0.7rem', color: '#059669', marginBottom: '6px', fontWeight: 600 }}>
                🐝 WASPs Activas ({wasps.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {wasps.map((wasp, idx) => (
                  <button
                    key={wasp.id}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                      backgroundColor: wasp.id === activeWaspId ? wasp.color : 'white',
                      color: wasp.id === activeWaspId ? 'white' : wasp.color,
                      border: `2px solid ${wasp.color}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      transition: 'all 0.2s'
                    }}
                    onClick={() => setActiveWaspId(wasp.id)}
                  >
                    {wasp.isMother ? '👑' : '🐝'} {idx === 0 ? 'M' : `H${idx}`}
                    {wasp.geoZone && ' ✓'}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '6px' }}>
                Triple-click en 👑 = Nueva hija | Triple-click en 🐝 = Eliminar
              </div>
            </div>
          )}

          {/* Info de WASP activa */}
          <div style={{
            padding: '8px',
            backgroundColor: `${activeWasp.color}15`,
            borderRadius: '6px',
            border: `1px solid ${activeWasp.color}`,
            marginBottom: '8px'
          }}>
            <div style={{ fontSize: '0.75rem', color: activeWasp.color, fontWeight: 600 }}>
              {activeWasp.isMother ? '👑 WASP Madre' : `🐝 WASP Hija #${wasps.filter(w => !w.isMother).findIndex(w => w.id === activeWasp.id) + 1}`}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
              Zona: {activeWasp.zone === 'map' ? '🗺️ Mapa' : '🎨 Canvas'}
              {activeWasp.geoZone ? ' | Geo: ✅' : ' | Geo: ❌'}
            </div>
            
            {/* Elementos asignados a esta WASP */}
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${activeWasp.color}30` }}>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '4px' }}>
                📦 Elementos asignados:
              </div>
              {activeWasp.assignedElementIds.length === 0 ? (
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontStyle: 'italic' }}>
                  Ninguno - arrastra elementos cerca de esta WASP
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {activeWasp.assignedElementIds.map(elId => {
                    const el = elements.find(e => e.id === elId);
                    if (!el) return null;
                    return (
                      <span
                        key={elId}
                        style={{
                          fontSize: '0.65rem',
                          padding: '2px 6px',
                          backgroundColor: el.color + '20',
                          color: el.color,
                          borderRadius: '4px',
                          border: `1px solid ${el.color}`,
                          fontWeight: 500
                        }}
                      >
                        {el.icon} {el.contactIds.length}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Info de zona de WASP activa */}
          {activeWasp.geoZone && (
            <>
              <div style={{
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                  Zona: {activeWasp.geoZone.type === 'circle' ? '🔵 Radio' : '📐 Polígono'}
                </div>
                {activeWasp.geoZone.type === 'circle' && activeWasp.geoZone.radius && (
                  <div style={{ fontSize: '0.85rem', color: activeWasp.color, fontWeight: 600, marginBottom: '4px' }}>
                    Radio: {Math.round(activeWasp.geoZone.radius)}m
                  </div>
                )}
                <div style={{ fontSize: '0.85rem', color: activeWasp.color, fontWeight: 600 }}>
                  Contactos en zona: {(() => {
                    // Solo contactos de elementos ASIGNADOS a esta WASP
                    const waspElementContacts: ContactDB[] = [];
                    activeWasp.assignedElementIds.forEach(elId => {
                      const element = elements.find(e => e.id === elId);
                      if (element) {
                        element.contactIds.forEach(id => {
                          let contact = contacts.find(c => c.id === id);
                          if (!contact) {
                            contact = favoritesContacts.find(c => c.id === id);
                          }
                          if (contact && !waspElementContacts.find(c => c.id === id)) {
                            waspElementContacts.push(contact);
                          }
                        });
                      }
                    });
                    return filterContactsByGeoZone(waspElementContacts, activeWasp.geoZone!).length;
                  })()}
                </div>
              </div>
              
              <button
                style={{
                  ...styles.btn,
                  width: '100%',
                  marginTop: '8px',
                  color: '#ef4444',
                  borderColor: '#ef4444',
                  justifyContent: 'center'
                }}
                onClick={() => {
                  // Borrar zona de WASP activa
                  if (activeWasp.geoZone?.layer) {
                    activeWasp.geoZone.layer.remove();
                  }
                  setWasps(prev => prev.map(w => 
                    w.id === activeWasp.id ? { ...w, geoZone: null } : w
                  ));
                  // También limpiar geoZone global si es la misma
                  if (geoZone?.layer === activeWasp.geoZone?.layer) {
                    setGeoZone(null);
                  }
                  setDrawingMode(null);
                }}
              >
                🗑️ Borrar Zona de {activeWasp.isMother ? 'Madre' : 'Hija'}
              </button>
            </>
          )}

          {/* Resumen Multi-Zona (OR lógico) */}
          {waspsWithZones.length > 1 && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #f59e0b'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, marginBottom: '6px' }}>
                📊 Resumen Multi-Zona (OR)
              </div>
              <div style={{ fontSize: '0.7rem', color: '#78350f' }}>
                {waspsWithZones.length} zonas activas
              </div>
              <div style={{ fontSize: '0.85rem', color: '#92400e', fontWeight: 700, marginTop: '4px' }}>
                Total contactos únicos: {(() => {
                  // OR lógico: contactos en zona de cada WASP, solo de sus elementos asignados
                  const contactsInAnyZone = new Set<string>();
                  waspsWithZones.forEach(wasp => {
                    if (wasp.geoZone) {
                      // Solo contactos de elementos ASIGNADOS a esta WASP
                      const waspElementContacts: ContactDB[] = [];
                      wasp.assignedElementIds.forEach(elId => {
                        const element = elements.find(e => e.id === elId);
                        if (element) {
                          element.contactIds.forEach(id => {
                            let contact = contacts.find(c => c.id === id);
                            if (!contact) {
                              contact = favoritesContacts.find(c => c.id === id);
                            }
                            if (contact && !waspElementContacts.find(c => c.id === id)) {
                              waspElementContacts.push(contact);
                            }
                          });
                        }
                      });
                      
                      const inZone = filterContactsByGeoZone(waspElementContacts, wasp.geoZone);
                      inZone.forEach(c => contactsInAnyZone.add(c.id));
                    }
                  });
                  return contactsInAnyZone.size;
                })()}
              </div>
            </div>
          )}

          {/* Backward compatibility: Info de zona global (geoZone) cuando no hay zona en WASP activa */}
          {geoZone && !activeWasp.geoZone && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                Zona Global: {geoZone.type === 'circle' ? '🔵 Radio' : '📐 Polígono'}
              </div>
              {geoZone.type === 'circle' && geoZone.radius && (
                <div style={{ fontSize: '0.85rem', color: '#2ca799', fontWeight: 600, marginBottom: '4px' }}>
                  Radio: {Math.round(geoZone.radius)}m
                </div>
              )}
              <button
                style={{
                  ...styles.btn,
                  width: '100%',
                  marginTop: '8px',
                  color: '#ef4444',
                  borderColor: '#ef4444',
                  justifyContent: 'center'
                }}
                onClick={() => {
                  if (geoZone.layer) {
                    geoZone.layer.remove();
                  }
                  setGeoZone(null);
                  setDrawingMode(null);
                }}
              >
                🗑️ Borrar Zona Global
              </button>
            </div>
          )}
        </div>
      )}

      <div style={styles.sidebarSection}>
        <h6 style={styles.sidebarTitle}>Estadísticas</h6>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
            <span>🎨 Pizarra:</span>
            <span style={{ color: '#2ca799', fontWeight: 600 }}>
              {elements.filter(e => e.zone === 'canvas').length}
            </span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
            <span>🗺️ Mapa:</span>
            <span style={{ color: '#2ca799', fontWeight: 600 }}>
              {elements.filter(e => e.zone === 'map').length}
            </span>
          </div>
          <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
            <span>📊 Total Elementos:</span>
            <span style={{ color: '#f59e0b', fontWeight: 600 }}>
              {elements.length}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>👥 Contactos DB:</span>
            <span style={{ color: '#374151', fontWeight: 600 }}>
              {contacts.length}
            </span>
          </div>
        </div>
      </div>

      {elements.length > 0 && (
        <div style={styles.sidebarSection}>
          <h6 style={styles.sidebarTitle}>Elementos Activos</h6>
          {elements.map(element => (
            <div
              key={element.id}
              style={{
                padding: '8px',
                marginBottom: '6px',
                backgroundColor: '#f9fafb',
                border: `1px solid ${element.color}`,
                borderRadius: '8px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onContextMenu={(e) => handleRightClick(e, element)}
              onClick={() => {
                if (element.zone === 'canvas') {
                  moveElementToMap(element.id);
                } else {
                  moveElementToCanvas(element.id);
                }
              }}
            >
              <div style={{ color: element.color, fontWeight: 600, marginBottom: '4px', fontSize: '0.875rem' }}>
                {element.icon} {element.label}
                {element.limit && ` (${element.limit})`}
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>
                📍 {element.zone === 'canvas' ? 'Pizarra' : 'Mapa'} | 
                👥 {element.contactIds.length} contactos
              </div>
              <div style={{ color: '#2ca799', fontSize: '0.65rem', marginTop: '4px' }}>
                {element.zone === 'canvas' ? '→ Click: Mover a Mapa' : '→ Click: Mover a Pizarra'}
                {element.type === 'all-contacts' && ' | Right-click: Ajustar límite'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: NETWORK VIEW (Canvas)
  // ───────────────────────────────────────────────────────────────────────

  const renderNetworkView = () => {
    const canvasElements = elements.filter(e => e.zone === 'canvas');

    return (
      <>
        {/* CAPA 1: Canvas/Pizarra - solo visual */}
        <div
          id="builder-canvas"
          ref={canvasRef}
          style={styles.canvas}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDropCanvas}
        >
          {/* Placeholder si no hay elementos */}
          {canvasElements.length === 0 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#6b7280',
              padding: '40px',
              maxWidth: '500px',
              zIndex: 2,
              pointerEvents: 'none' as const
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px', color: '#2ca799' }}>
                <FontAwesomeIcon icon={faNetworkWired} />
              </div>
              <h3 style={{ marginBottom: '16px', color: '#374151', fontWeight: 600 }}>
                Network View - Constructor de Audiencias
              </h3>
              <p style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                Arrastra <strong style={{ color: '#f59e0b' }}>⭐ Favoritos</strong> o <strong style={{ color: '#2ca799' }}>👥 Todos</strong> aquí para crear tu red de contactos.<br/>
                Las conexiones fluirán hacia el nodo central WASP.<br/><br/>
                {showMap ? (
                  <span style={{ color: '#2ca799' }}>
                    O arrástralos al <strong>MAPA</strong> para visualización geográfica →
                  </span>
                ) : (
                  <span style={{ color: '#6b7280' }}>
                    Activa el <strong>MAPA</strong> para vista híbrida
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </>
    );
  };

  // Renderizar overlays (SVG + Elementos) - fuera del canvas
  const renderOverlays = () => {
    const canvasElements = elements.filter(e => e.zone === 'canvas');
    const mapElements = elements.filter(e => e.zone === 'map'); 
    
    return (
      <>
        {/* CAPA 3: SVG Overlay - Líneas y partículas */}
        <svg style={styles.svgOverlay}>
          {/* Conexiones elemento → WASP dueña */}
          {elements.map(element => {
            // Buscar la WASP dueña de este elemento
            const ownerWasp = wasps.find(w => w.assignedElementIds.includes(element.id));
            if (!ownerWasp) return null;
            
            const path = `M ${element.position.x} ${element.position.y} Q ${element.position.x + (ownerWasp.x - element.position.x) / 2} ${element.position.y - 100}, ${ownerWasp.x} ${ownerWasp.y}`;
            
            return (
              <g key={`connection-${element.id}`}>
                <path
                  d={path}
                  stroke={ownerWasp.color}
                  strokeWidth="2"
                  fill="none"
                  opacity="0.3"
                />
                <path
                  d={path}
                  stroke={ownerWasp.color}
                  strokeWidth="2"
                  fill="none"
                  opacity="0.8"
                  strokeDasharray="10,5"
                  style={{ animation: 'dataFlow 1s linear infinite' }}
                />
              </g>
            );
          })}

          {particles.map(particle => {
            // Buscar elemento y su WASP dueña
            const element = elements.find(e => e.id === particle.elementId);
            if (!element) return null;
            
            const ownerWasp = wasps.find(w => w.assignedElementIds.includes(element.id));
            if (!ownerWasp) return null;
            
            const pos = getBezierPoint(particle.progress, element.position, { x: ownerWasp.x, y: ownerWasp.y });
              return (
                <circle
                  key={particle.id}
                  cx={pos.x}
                  cy={pos.y}
                  r="3"
                  fill={ownerWasp.color}
                  opacity="0.9"
                  style={{ filter: `drop-shadow(0 0 4px ${ownerWasp.color})` }}
                />
              );
          })}
        </svg>

        {/* CAPA 4: Elementos drag&drop */}
        <div style={styles.elementsLayer}>
          {/* MULTI-WASP: Renderizar TODAS las WASPs v3.7.0 */}
          {wasps.map(wasp => (
            <div
              key={wasp.id}
              className="wasp-node"
              style={{
                position: 'absolute',
                left: `${wasp.x - wasp.size / 2}px`,
                top: `${wasp.y - wasp.size / 2}px`,
                width: `${wasp.size}px`,
                height: `${wasp.size}px`,
                cursor: finalized ? 'default' : 'move',
                pointerEvents: 'auto' as const,
                opacity: wasp.zone === 'map' ? 0.9 : 1,
                // Glow para WASP activa
                filter: wasp.id === activeWaspId 
                  ? `drop-shadow(0 0 12px ${wasp.color})` 
                  : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                // Escala para indicar activa
                transform: wasp.id === activeWaspId ? 'scale(1.05)' : 'scale(1)',
                transition: 'filter 0.2s, transform 0.2s',
                zIndex: wasp.id === activeWaspId ? 101 : 100
              }}
              onClick={(e) => handleWaspClick(wasp.id, e)}
              onMouseDown={(e) => {
                if (finalized) return;
                e.stopPropagation();
                setWasps(prev => prev.map(w => 
                  w.id === wasp.id ? { ...w, dragging: true } : w
                ));
                setActiveWaspId(wasp.id);
                // Backward compatibility: también actualizar waspNode
                if (wasp.isMother) {
                  setWaspNode(prev => ({ ...prev, dragging: true }));
                }
              }}
              dangerouslySetInnerHTML={{ __html: wasp.isMother ? WASP_NODE_SVG : WASP_NODE_SVG.replace(/#2ca799/g, wasp.color).replace(/#238a7f/g, wasp.color) }}
            />
          ))}

          {/* Indicador de WASP activa - label */}
          {wasps.length > 1 && (
            <div
              style={{
                position: 'absolute',
                left: `${activeWasp.x - 40}px`,
                top: `${activeWasp.y + activeWasp.size / 2 + 5}px`,
                backgroundColor: activeWasp.color,
                color: 'white',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.65rem',
                fontWeight: 600,
                pointerEvents: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              {activeWasp.isMother ? '👑 MADRE' : `🐝 HIJA #${wasps.filter(w => !w.isMother).findIndex(w => w.id === activeWasp.id) + 1}`}
              {activeWasp.geoZone && ' ✓'}
            </div>
          )}

          {/* Elementos de Contactos */}
          {canvasElements.map(element => (
            <div
              key={element.id}
              className="contact-element"
              style={{
                position: 'absolute',
                left: `${element.position.x - 60}px`,
                top: `${element.position.y - 30}px`,
                padding: '12px 16px',
                backgroundColor: 'white',
                border: `2px solid ${element.color}`,
                borderRadius: '8px',
                color: element.color,
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`,
                cursor: finalized ? 'default' : 'move',
                userSelect: 'none' as const,
                pointerEvents: 'auto' as const
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element.id)}
              onContextMenu={(e) => handleRightClick(e, element)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.5rem' }}>{element.icon}</span>
                <div>
                  <div>{element.label}</div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 'normal' }}>
                    {element.contactIds.length} contactos
                    {element.limit && ` (${element.limit})`}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Elementos Mapa */}
          {mapElements.map(element => (
            <div
              key={element.id}
              className="contact-element"
              style={{
                position: 'absolute',
                left: `${element.position.x - 60}px`,
                top: `${element.position.y - 30}px`,
                padding: '12px 16px',
                backgroundColor: 'white',
                border: `2px solid ${element.color}`,
                borderRadius: '8px',
                color: element.color,
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`,
                cursor: finalized ? 'default' : 'move',
                userSelect: 'none' as const,
                pointerEvents: 'auto' as const
              }}
              onMouseDown={(e) => handleElementMouseDown(e, element.id)}
              onContextMenu={(e) => handleRightClick(e, element)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.5rem' }}>{element.icon}</span>
                <div>
                  <div>{element.label}</div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 'normal' }}>
                    {element.contactIds.length} contactos
                    {element.limit && ` (${element.limit})`}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MAP
  // ───────────────────────────────────────────────────────────────────────

  const renderMap = () => (
    <div
      ref={mapContainerRef}
      style={styles.mapContainer}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDropMap}
    >
      <div id="geomap-canvas" style={styles.mapWrapper}></div>
      
      {/* Overlays de zonas válidas - Logo WASP translúcido + check */}
      {validZoneWaspIds.map(waspId => {
        const wasp = wasps.find(w => w.id === waspId);
        if (!wasp || !wasp.geoZone) return null;
        
        // Calcular centro de la zona para posicionar el overlay
        let centerX = 0;
        let centerY = 0;
        
        if (wasp.geoZone.type === 'circle') {
          // Para círculos, usar el centro
          if (mapRef.current) {
            const point = mapRef.current.latLngToContainerPoint([
              wasp.geoZone.coordinates[0].lat,
              wasp.geoZone.coordinates[0].lng
            ]);
            centerX = point.x;
            centerY = point.y;
          }
        } else {
          // Para polígonos, calcular centroide
          if (mapRef.current && wasp.geoZone.coordinates.length > 0) {
            const coords = wasp.geoZone.coordinates;
            const avgLat = coords.reduce((sum, c) => sum + c.lat, 0) / coords.length;
            const avgLng = coords.reduce((sum, c) => sum + c.lng, 0) / coords.length;
            const point = mapRef.current.latLngToContainerPoint([avgLat, avgLng]);
            centerX = point.x;
            centerY = point.y;
          }
        }
        
        return (
          <div
            key={`valid-zone-${waspId}`}
            style={{
              position: 'absolute',
              left: `${centerX - 40}px`,
              top: `${centerY - 40}px`,
              width: '80px',
              height: '80px',
              pointerEvents: 'none',
              zIndex: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.7
            }}
          >
            <img 
              src={wasplogo} 
              alt="WASP Zone Valid" 
              style={{
                width: '50px',
                height: '50px',
                opacity: 0.6,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
            />
            <div style={{
              backgroundColor: '#10b981',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              marginTop: '-10px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              border: '2px solid white'
            }}>
              ✓
            </div>
          </div>
        );
      })}
      
      {zoneWarning && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#fef2f2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          padding: '12px 20px',
          color: '#991b1b',
          fontWeight: 600,
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          {zoneWarning}
        </div>
      )}
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: LIMIT MODAL
  // ───────────────────────────────────────────────────────────────────────

  const renderLimitModal = () => {
    if (!showLimitModal || !limitModalElement) return null;

    return (
      <>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999
          }}
          onClick={() => setShowLimitModal(false)}
        />
        <div style={styles.limitModal}>
          <h3 style={{
            color: '#374151',
            marginBottom: '20px',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '1.25rem'
          }}>
            Ajustar Límite de Contactos
          </h3>
          <p style={{
            color: '#6b7280',
            fontSize: '0.9rem',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            Actual: <strong style={{ color: '#2ca799' }}>{limitModalElement.limit}</strong> contactos
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            {CONTACT_LIMITS.map(limit => (
              <button
                key={limit}
                className="limit-button"
                style={{
                  ...styles.limitButton,
                  backgroundColor: limit === limitModalElement.limit 
                    ? 'rgba(44, 167, 153, 0.1)' 
                    : 'white',
                  borderColor: limit === limitModalElement.limit
                    ? '#2ca799'
                    : '#e5e7eb',
                  color: limit === limitModalElement.limit
                    ? '#2ca799'
                    : '#374151'
                }}
                onClick={() => handleLimitChange(limit)}
              >
                {limit}
              </button>
            ))}
          </div>
          <p style={{
            color: '#f59e0b',
            fontSize: '0.75rem',
            textAlign: 'center',
            marginTop: '20px',
            marginBottom: 0
          }}>
            ⚠️ Mínimo: 1 | Máximo: 300
          </p>
        </div>
      </>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: CONFIRM MODAL
  // ───────────────────────────────────────────────────────────────────────

  const renderConfirmModal = () => {
  if (!showConfirmModal) return null;

  // Calcular contactos finales que se enviarán - MULTI-WASP v3.7.0
  let finalContacts = new Set<string>();
  let zoneType = 'Polígono'; // Default
  
  // Verificar si hay WASPs en modo mapa con zonas
  const waspsInMapWithZones = wasps.filter(w => w.zone === 'map' && w.geoZone);
  
  if (waspsInMapWithZones.length > 0) {
    // GEOMARKETING MULTI-ZONA: OR lógico entre zonas
    waspsInMapWithZones.forEach(wasp => {
      if (wasp.geoZone) {
        // Determinar tipo de zona para mostrar
        if (wasp.geoZone.type === 'circle') {
          zoneType = 'Radio';
        }
        
        // Solo contactos de elementos ASIGNADOS a esta WASP
        const waspElementContacts: ContactDB[] = [];
        wasp.assignedElementIds.forEach(elId => {
          const element = elements.find(e => e.id === elId);
          if (element) {
            element.contactIds.forEach(id => {
              let contact = contacts.find(c => c.id === id);
              if (!contact) {
                contact = favoritesContacts.find(c => c.id === id);
              }
              if (contact && !waspElementContacts.find(c => c.id === id)) {
                waspElementContacts.push(contact);
              }
            });
          }
        });
        
        const contactsInThisZone = filterContactsByGeoZone(waspElementContacts, wasp.geoZone);
        contactsInThisZone.forEach(c => finalContacts.add(c.id));
      }
    });
  } else if (waspNode.zone === 'map' && geoZone && waspNode.mapPosition) {
    // BACKWARD COMPATIBILITY: Zona global
    const allElementContacts: ContactDB[] = [];
    elements.forEach(el => {
      el.contactIds.forEach(id => {
        let contact = contacts.find(c => c.id === id);
        if (!contact) {
          contact = favoritesContacts.find(c => c.id === id);
        }
        if (contact && !allElementContacts.find(c => c.id === id)) {
          allElementContacts.push(contact);
        }
      });
    });
    
    const contactsInZone = filterContactsByGeoZone(allElementContacts, geoZone);
    contactsInZone.forEach(c => finalContacts.add(c.id));
    zoneType = geoZone.type === 'circle' ? 'Radio' : 'Polígono';
  } else {
    // MASIVO: Todos los contactos de elementos
    elements.forEach(e => e.contactIds.forEach(id => finalContacts.add(id)));
  }
  
  const isGeoMode = waspsInMapWithZones.length > 0 || (waspNode.zone === 'map' && geoZone);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10001
        }}
        onClick={() => setShowConfirmModal(false)}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '32px',
        zIndex: 10002,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        minWidth: '450px',
        maxWidth: '500px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ 
            fontSize: '3rem', 
            marginBottom: '16px',
            color: '#2ca799'
          }}>
            {isGeoMode ? '🎯' : '📡'}
          </div>
          <h3 style={{
            color: '#374151',
            marginBottom: '12px',
            fontWeight: 600,
            fontSize: '1.5rem'
          }}>
            Finalizar Selección
          </h3>
          <p style={{
            color: '#6b7280',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            marginBottom: '24px'
          }}>
            {isGeoMode 
              ? 'Envío GEOMARKETING: Solo contactos en zona seleccionada.'
              : 'Envío MASIVO: Todos los contactos de elementos seleccionados.'}
          </p>
        </div>

        <div style={{
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '12px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Modo:</span>
            <span style={{ color: '#2ca799', fontWeight: 600, fontSize: '0.95rem' }}>
              {isGeoMode ? '🎯 Geomarketing' : '📡 Masivo'}
            </span>
          </div>
          
          {isGeoMode && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Zonas activas:</span>
              <span style={{ color: '#374151', fontWeight: 600, fontSize: '0.95rem' }}>
                {waspsInMapWithZones.length > 0 
                  ? `${waspsInMapWithZones.length} zona${waspsInMapWithZones.length > 1 ? 's' : ''} (${zoneType})`
                  : `1 zona (${zoneType})`
                }
              </span>
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between'
          }}>
            <span style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 500 }}>Contactos a enviar:</span>
            <span style={{ 
              color: finalContacts.size > 0 ? '#2ca799' : '#ef4444', 
              fontWeight: 700, 
              fontSize: '1.25rem'
            }}>
              {finalContacts.size}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            style={{
              flex: 1,
              padding: '12px 24px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onClick={() => setShowConfirmModal(false)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            Cancelar
          </button>
          <button
            style={{
              flex: 1,
              padding: '12px 24px',
              backgroundColor: '#2ca799',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}
            onClick={handleConfirmFinalize}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#238a7f';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2ca799';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
            }}
          >
            Confirmar Envío
          </button>
        </div>
      </div>
    </>
  );
};

  // ───────────────────────────────────────────────────────────────────────
  // MOUSE MOVE (WASP Node Dragging - Canvas ↔ Map) - MULTI-WASP v3.7.0
  // ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Buscar si hay alguna WASP arrastrándose
    const draggingWasp = wasps.find(w => w.dragging);
    const isAnyDragging = draggingWasp || waspNode.dragging;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isAnyDragging) return;

      // Determinar si el cursor está sobre canvas o mapa
      let overCanvas = false;
      let overMap = false;

      if (canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        overCanvas = e.clientX >= canvasRect.left && 
                    e.clientX <= canvasRect.right &&
                    e.clientY >= canvasRect.top &&
                    e.clientY <= canvasRect.bottom;
      }

      if (showMap && mapContainerRef.current) {
        const mapRect = mapContainerRef.current.getBoundingClientRect();
        overMap = e.clientX >= mapRect.left && 
                 e.clientX <= mapRect.right &&
                 e.clientY >= mapRect.top &&
                 e.clientY <= mapRect.bottom;
      }

      // Calcular posición
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // MULTI-WASP: Actualizar la WASP que está siendo arrastrada
        if (draggingWasp) {
          setWasps(prev => prev.map(w => 
            w.id === draggingWasp.id 
              ? { ...w, x, y }
              : w
          ));
        }

        // Backward compatibility: también actualizar waspNode si es la madre
        if (waspNode.dragging || (draggingWasp && draggingWasp.isMother)) {
          setWaspNode(prev => ({
            ...prev,
            x: x,
            y: y
          }));
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isAnyDragging) return;

      // Determinar automáticamente si está sobre mapa o canvas
      let finalZone: 'canvas' | 'map' = 'canvas';
      let mapPosition = null;

      if (showMap && mapContainerRef.current && mapRef.current) {
        const mapRect = mapContainerRef.current.getBoundingClientRect();
        const overMap = e.clientX >= mapRect.left && 
                       e.clientX <= mapRect.right &&
                       e.clientY >= mapRect.top &&
                       e.clientY <= mapRect.bottom;

        if (overMap) {
          const L = (window as any).L;
          if (L) {
            const x = e.clientX - mapRect.left;
            const y = e.clientY - mapRect.top;
            const point = L.point(x, y);
            const latlng = mapRef.current.containerPointToLatLng(point);
            
            finalZone = 'map';
            mapPosition = { lat: latlng.lat, lng: latlng.lng };
          }
        }
      }

      // Calcular posición final
      if (canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - canvasRect.left;
        const y = e.clientY - canvasRect.top;

        let finalX = x;
        let finalY = y;

        if (finalZone === 'canvas') {
          const size = draggingWasp?.size || waspNode.size;
          finalX = Math.max(size / 2, Math.min(canvasRect.width - size / 2, x));
          finalY = Math.max(size / 2, Math.min(canvasRect.height - size / 2, y));
        }

        // MULTI-WASP: Actualizar la WASP que se estaba arrastrando
        if (draggingWasp) {
          // Si es la madre y está volviendo a canvas, destruir hijas
          const wasMotherInMap = draggingWasp.isMother && draggingWasp.zone === 'map';
          const isGoingToCanvas = finalZone === 'canvas';
          
          if (wasMotherInMap && isGoingToCanvas) {
            // Madre vuelve a pizarra → destruir todas las hijas con animación Galaga
            console.log('👑 Madre vuelve a pizarra - destruyendo hijas...');
            destroyAllChildWasps();
          }

          setWasps(prev => prev.map(w => 
            w.id === draggingWasp.id 
              ? { 
                  ...w, 
                  x: finalX, 
                  y: finalY, 
                  dragging: false,
                  zone: finalZone,
                  mapPosition: mapPosition || w.mapPosition
                }
              : w
          ));

          console.log(`🐝 WASP ${draggingWasp.id} → ${finalZone === 'map' ? '🎯 MODO GEOMARKETING' : '📡 MODO MASIVO'}`);
        }

        // Backward compatibility: también actualizar waspNode
        setWaspNode(prev => ({
          ...prev,
          x: finalX,
          y: finalY,
          dragging: false,
          zone: finalZone,
          mapPosition: mapPosition || prev.mapPosition
        }));

        console.log(finalZone === 'map' 
          ? `🎯 WASP en MODO GEOMARKETING` 
          : `📡 WASP en MODO MASIVO`
        );
      } else {
        // Resetear dragging en todas las WASPs
        setWasps(prev => prev.map(w => ({ ...w, dragging: false })));
        setWaspNode(prev => ({ ...prev, dragging: false }));
      }
    };

    if (isAnyDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [wasps, waspNode.dragging, showMap, finalized]);

  // ───────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────

  const totalSelectedContacts = useMemo(() => {
    const unique = new Set<string>();
    elements.forEach(e => e.contactIds.forEach(id => unique.add(id)));
    return unique.size;
  }, [elements]);

  const canFinalize = useMemo(() => {
    if (elements.length === 0) return false;
    
    // ═══════════════════════════════════════════════════════════════════════
    // MULTI-WASP: Verificar si hay alguna WASP con zona válida
    // ═══════════════════════════════════════════════════════════════════════
    
    const waspsInMapWithZones = wasps.filter(w => w.zone === 'map' && w.geoZone);
    
    if (waspsInMapWithZones.length > 0) {
      // Hay WASPs con zonas → verificar que haya contactos en al menos una zona
      const allElementContacts: ContactDB[] = [];
      elements.forEach(el => {
        el.contactIds.forEach(id => {
          let contact = contacts.find(c => c.id === id);
          if (!contact) {
            contact = favoritesContacts.find(c => c.id === id);
          }
          if (contact && !allElementContacts.find(c => c.id === id)) {
            allElementContacts.push(contact);
          }
        });
      });
      
      // OR lógico: si hay contactos en CUALQUIER zona, se puede finalizar
      const hasContactsInAnyZone = waspsInMapWithZones.some(wasp => {
        if (!wasp.geoZone) return false;
        const contactsInZone = filterContactsByGeoZone(allElementContacts, wasp.geoZone);
        return contactsInZone.length > 0;
      });
      
      return hasContactsInAnyZone;
    }
    
    // Backward compatibility: verificar zona global (geoZone)
    if (waspNode.zone === 'map') {
      if (!geoZone) return false;
      if (!waspNode.mapPosition) return false;
      
      const waspInside = geoZone.type === 'circle'
        ? isPointInCircle(waspNode.mapPosition, geoZone.coordinates[0], geoZone.radius!)
        : isPointInPolygon(waspNode.mapPosition, geoZone.coordinates);
      
      if (!waspInside) return false;
      
      const allElementContacts: ContactDB[] = [];
      elements.forEach(el => {
        el.contactIds.forEach(id => {
          let contact = contacts.find(c => c.id === id);
          if (!contact) {
            contact = favoritesContacts.find(c => c.id === id);
          }
          if (contact && !allElementContacts.find(c => c.id === id)) {
            allElementContacts.push(contact);
          }
        });
      });
      
      const contactsInZone = filterContactsByGeoZone(allElementContacts, geoZone);
      return contactsInZone.length > 0;
    }
    
    // Modo masivo: siempre se puede finalizar si hay elementos
    return true;
  }, [wasps, waspNode.zone, waspNode.mapPosition, geoZone, elements, contacts, favoritesContacts]);

  return (
    <>
      <style>{responsiveStyles}</style>
      
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            <FontAwesomeIcon icon={faProjectDiagram} />
            Constructor de Audiencias
            
            {/* Indicador de modo - MULTI-WASP v3.7.0 */}
            <div style={{
              backgroundColor: wasps.some(w => w.zone === 'map') ? '#fef3c7' : '#f9fafb',
              padding: '6px 14px',
              borderRadius: '10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.8rem',
              fontWeight: 600,
              border: wasps.some(w => w.zone === 'map') ? '1px solid #f59e0b' : '1px solid #e5e7eb'
            }}>
              {wasps.some(w => w.zone === 'map') ? (
                <>
                  <span style={{ fontSize: '1.1rem' }}>🎯</span>
                  <span style={{ color: '#f59e0b' }}>MODO GEOMARKETING</span>
                  {wasps.length > 1 && (
                    <span style={{ 
                      backgroundColor: '#10b981', 
                      color: 'white', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      marginLeft: '4px'
                    }}>
                      {wasps.length} WASPs
                    </span>
                  )}
                  {waspsWithZones.length > 0 && (
                    <span style={{ color: '#2ca799', marginLeft: '4px' }}>
                      | {waspsWithZones.length} zona{waspsWithZones.length > 1 ? 's' : ''}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ fontSize: '1.1rem' }}>📡</span>
                  <span style={{ color: '#6b7280' }}>MODO MASIVO</span>
                </>
              )}
            </div>

            {totalSelectedContacts > 0 && (
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: 'rgba(44, 167, 153, 0.1)',
                color: '#2ca799',
                padding: '4px 12px',
                borderRadius: '12px',
                border: '1px solid #2ca799'
              }}>
                {totalSelectedContacts} contactos
              </span>
            )}
          </h2>

          <div style={styles.headerActions}>
            {elements.length > 0 && !finalized && (
  <button
    style={{ 
      ...styles.btn, 
      ...styles.btnPrimary,
      opacity: canFinalize ? 1 : 0.5,
      cursor: canFinalize ? 'pointer' : 'not-allowed'
    }}
    onClick={handleFinalize}
    disabled={!canFinalize}
  >
    <FontAwesomeIcon icon={faCheckCircle} />
    FINALIZAR SELECCIÓN
  </button>
)}

            <button
              style={{ ...styles.btn, ...styles.btnIcon }}
              onClick={onClose}
              title="Cerrar"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.mainContent}>
          {renderSidebar()}

          <div style={styles.splitView}>
            {renderNetworkView()}
            {showMap && renderMap()}
            {renderOverlays()}
          </div>
        </div>

        {/* Modals */}
        {renderLimitModal()}
        {renderConfirmModal()}
      </div>
    </>
  );
};

export default ContactsGeoBuilder;